import React, { useEffect, useState } from 'react'
import {
  Button,
  Dialog,
  DialogActions,
  DialogTitle,
  IconButton,
  Typography,
} from '@material-ui/core'
import CloseIcon from '@material-ui/icons/Close'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'
import { eulaApi, EulaSection } from '../../services/api/eula'
import { Links } from '../../settings/links'
import {
  selectCheckedAuth,
  selectIsAuthenticated,
} from '../../store/auth/selectors'
import {
  eulaDialogBackdropStyle,
  useEulaAgreementDialogStyles,
} from './eula-agreement-dialog.styles'
import { parseEulaContent } from './eula-agreement-dialog.utils'

export const EulaAgreementDialog: React.FC = () => {
  const { t } = useTranslation()
  const classes = useEulaAgreementDialogStyles()
  const isAuthenticated = useSelector(selectIsAuthenticated)
  const checkedAuth = useSelector(selectCheckedAuth)
  const [isOpen, setIsOpen] = useState(false)
  const [sections, setSections] = useState<EulaSection[]>([])
  const [isAccepting, setIsAccepting] = useState(false)

  useEffect(() => {
    if (!checkedAuth || !isAuthenticated) {
      return
    }

    let cancelled = false

    const loadEula = async () => {
      try {
        const latest = await eulaApi.getLatestAsync()

        if (cancelled || !latest.content) {
          return
        }

        setSections(parseEulaContent(latest.content))
        setIsOpen(true)
      } catch {
        // Do not block the app when EULA is unavailable.
      }
    }

    void loadEula()

    return () => {
      cancelled = true
    }
  }, [checkedAuth, isAuthenticated])

  const handleReject = () => {
    window.location.assign(Links.productionPage)
  }

  const handleAccept = async () => {
    setIsAccepting(true)

    try {
      await eulaApi.acceptAsync()
      setIsOpen(false)
    } catch {
      setIsAccepting(false)
    }
  }

  if (!checkedAuth || !isAuthenticated || !isOpen) {
    return null
  }

  return (
    <Dialog
      open={isOpen}
      onClose={handleReject}
      maxWidth="sm"
      fullWidth
      disableEscapeKeyDown
      classes={{ paper: classes.dialogPaper }}
      BackdropProps={{ style: eulaDialogBackdropStyle }}
      aria-labelledby="eula-agreement-title"
      aria-describedby="eula-agreement-description"
    >
      <DialogTitle id="eula-agreement-title" className={classes.dialogTitle}>
        {t('eulaAgreement.title')}
        <IconButton
          aria-label={t('eulaAgreement.close')}
          className={classes.closeButton}
          onClick={handleReject}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <Typography component="p" className={classes.subtitle}>
        {t('eulaAgreement.subtitle')}
      </Typography>

      <div id="eula-agreement-description" className={classes.scrollPanel}>
        {sections.map((section, index) => (
          <div
            key={section.title || `section-${index}`}
            className={classes.section}
          >
            {section.title ? (
              <Typography component="h3" className={classes.sectionTitle}>
                {section.title}
              </Typography>
            ) : null}
            <Typography component="p" className={classes.sectionBody}>
              {section.body}
            </Typography>
          </div>
        ))}
      </div>

      <Typography component="p" className={classes.acceptNote}>
        {t('eulaAgreement.acceptNote')}
      </Typography>

      <DialogActions className={classes.actions}>
        <Button
          variant="outlined"
          color="primary"
          className={classes.rejectButton}
          onClick={handleReject}
          disabled={isAccepting}
        >
          {t('eulaAgreement.reject')}
        </Button>
        <Button
          variant="contained"
          color="primary"
          className={classes.acceptButton}
          onClick={handleAccept}
          disabled={isAccepting}
        >
          {t('eulaAgreement.accept')}
        </Button>
      </DialogActions>
    </Dialog>
  )
}
